// Top workloads by monthly cost or waste — the efficiency table. Sortable columns,
// inline efficiency bars, CSV export. Reads /finops/v2/.../workloads/top.

import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Download, ArrowUpDown } from 'lucide-react';
import { useTopWorkloads } from '../../hooks/useFinopsV2';
import { formatMoney, formatCores, formatGB, downloadCsv } from './finopsFormat';
import { SectionHeader, Segmented, UtilBar, Skeleton, ChDisabledCard, EmptyCard } from './finopsUi';

const COLS = [
  { key: 'workload', label: 'Workload', align: 'left' },
  { key: 'monthly_cost_usd', label: 'Cost/mo', align: 'right', fmt: (v) => formatMoney(v) },
  { key: 'monthly_waste_usd', label: 'Waste/mo', align: 'right', fmt: (v) => formatMoney(v) },
  { key: 'efficiency_pct', label: 'Efficiency', align: 'right' },
  { key: 'cpu_requested', label: 'CPU req', align: 'right', fmt: (v) => formatCores(v) },
  { key: 'mem_requested_gb', label: 'Mem req', align: 'right', fmt: (v) => formatGB(v) },
  { key: 'pods', label: 'Pods', align: 'right' },
];

export default function TopWorkloadsTable({ clusterId, hours, limit = 25 }) {
  const [sort, setSort] = useState('waste'); // server sort (cost|waste)
  const [colSort, setColSort] = useState({ key: null, dir: 'desc' });
  const { data, loading, available } = useTopWorkloads(clusterId, hours, sort, limit);

  const rows = useMemo(() => {
    const items = (data?.items || []).slice();
    if (colSort.key) {
      items.sort((a, b) => {
        const av = a[colSort.key], bv = b[colSort.key];
        if (typeof av === 'string') return colSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return colSort.dir === 'asc' ? (av ?? 0) - (bv ?? 0) : (bv ?? 0) - (av ?? 0);
      });
    }
    return items;
  }, [data, colSort]);

  const onHeader = (key) =>
    setColSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const exportCsv = () => downloadCsv(`workloads-${clusterId}.csv`, [
    { key: 'namespace', label: 'Namespace' }, { key: 'workload', label: 'Workload' },
    { key: 'monthly_cost_usd', label: 'Monthly Cost USD' }, { key: 'monthly_waste_usd', label: 'Monthly Waste USD' },
    { key: 'efficiency_pct', label: 'Efficiency %' }, { key: 'cpu_requested', label: 'CPU Requested' },
    { key: 'cpu_p95_used', label: 'CPU p95 Used' }, { key: 'mem_requested_gb', label: 'Mem Requested GB' },
    { key: 'pods', label: 'Pods' },
  ], rows);

  if (!available) return <ChDisabledCard title="Workload efficiency unavailable" />;
  if (loading) return <Skeleton className="h-72" />;
  if (!rows.length) return <EmptyCard message="No workloads with data in this window" />;

  return (
    <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
      <CardContent className="p-5">
        <SectionHeader title="Top Workloads" hint="Requested vs p95-used — the rightsizing headroom">
          <Segmented options={[{ value: 'waste', label: 'By waste' }, { value: 'cost', label: 'By cost' }]}
                     value={sort} onChange={setSort} />
          <button onClick={exportCsv}
                  className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-text-secondary hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-surface-border rounded-md px-2 py-1">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </SectionHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-surface-border">
                {COLS.map((c) => (
                  <th key={c.key}
                      className={`py-2 px-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                    <button onClick={() => onHeader(c.key)}
                            className={`inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-white ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                      {c.label}<ArrowUpDown className="w-3 h-3 opacity-40" />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.namespace}/${r.workload}/${i}`}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="py-2 px-2">
                    <div className="font-medium text-slate-900 dark:text-white truncate max-w-[220px]">{r.workload}</div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 truncate max-w-[220px]">{r.namespace}</div>
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-900 dark:text-white">{formatMoney(r.monthly_cost_usd)}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-amber-600 dark:text-amber-400">{formatMoney(r.monthly_waste_usd)}</td>
                  <td className="py-2 px-2"><div className="flex justify-end"><UtilBar pct={r.efficiency_pct} tint="bg-emerald-500" /></div></td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{formatCores(r.cpu_requested)}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{formatGB(r.mem_requested_gb)}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{r.pods}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
